/**
 * CueScheduler.js — THE one runtime scheduler for the board grammar (Task #347,
 * THE BOARD GRAMMAR Tier 1 item 1).
 *
 * Law 1: narration is the only clock. Every timed board performance — reveals,
 * marks, pans, direction, tweens (all later tiers) — registers a performer
 * here and fires off the narration audio playhead. Nothing else in the player
 * may schedule against wall-clock time.
 *
 * This module is deliberately environment-free (no window/document use at
 * module scope) because it is imported from BOTH:
 *   - the /learn player (TutorStageController) — the runtime, and
 *   - scripts/validate_learn_course.mjs — the content validator,
 * exactly like FractionWidgets/FractionGrading, so the anchor grammar the
 * validator enforces and the grammar the runtime consumes are the same code.
 * The Python mirror is lesson_anchors.py + narration_alignment.py (server
 * owns tokenization — Trap 6; shared fixtures under tests/cues/ keep parity).
 *
 * The closed anchor grammar (`at`):
 *   {"word": 14}                      stored token index; RESOLVED when the
 *                                     publish-time resolver added
 *                                     {"tMs": int, "align": "<hash>"}
 *   {"word": "monstrous"}             authoring sugar — publish-time only;
 *                                     at play it degrades to beat start
 *   {"after": "blk_2"}                board event — fired via onBoardEvent()
 *
 * Degradation law (all LOUD in debug, all safe):
 *   - no timing_data for the line       → every word cue fires at line start
 *   - align hash ≠ timing hash (rot)    → that cue fires at line start
 *   - unresolved/sugar anchor           → fires at line start
 *   - zero performers registered        → nothing observable happens at all
 *     (today's behavior, byte-identical)
 *
 * Seek determinism (Trap 3): board state must be a pure function of
 * (playhead, graded answers). Performers MUST be idempotent projections —
 * `projectAt(t)` re-applies every cue due at t in "project" mode and is the
 * only sanctioned way to rebuild state on replay/prev/jump. No performer may
 * take a fire-and-forget side effect that projectAt cannot reproduce.
 *
 * Clock-hold contract (the pause primitive, Trap 2):
 *   hold()    — the lesson clock is HELD: no beat advance/chaining may start
 *               while isHeld() (the controller consults it before chaining).
 *               The SPEAKER IS NEVER SUSPENDED — sacred audio rule — so an
 *               in-flight narration line keeps playing and its cues KEEP
 *               FIRING (the board must never desync from audible narration).
 *   release() — chaining may resume.
 *   Holds are COUNTED (Task #353): independent holders — an open check/hold
 *   interaction, a modal — stack hold()/release() pairs without stealing each
 *   other's release; the clock runs only when every holder has released.
 */

export const CUE_SCHEMA_VERSION = 9; // mirror of lesson_anchors.LESSON_SCHEMA_VERSION
// Grammar waves (migration lint, Trap 9): `at` anchors arrived in v2; the
// reveal grammar (`fx` + fragment staging, Task #348) arrived in v3; the mark
// grammar (`board.marks`, Task #350) arrived in v4; the direction grammar
// (`beat.direction` authored overrides, Task #351) arrived in v5; the plate
// grammar (primary sources on the board, Task #352) arrived in v6; the hold
// grammar (`hold` predict-pause beats, Task #353) arrived in v7; the memory
// grammar (`echo` distractor feedback + `pin` rail + student tokens, Task
// #354) arrived in v8; the custom-widget grammar (`custom` sandboxed
// authored-widget beats) arrived in v9. Content only needs the version of
// the newest grammar it actually uses.
export const ANCHOR_MIN_SCHEMA_VERSION = 2;
export const REVEAL_MIN_SCHEMA_VERSION = 3;
export const MARK_MIN_SCHEMA_VERSION = 4;
export const DIRECTION_MIN_SCHEMA_VERSION = 5;
export const PLATE_MIN_SCHEMA_VERSION = 6;
export const HOLD_MIN_SCHEMA_VERSION = 7;
export const MEMORY_MIN_SCHEMA_VERSION = 8;
export const CUSTOM_MIN_SCHEMA_VERSION = 9;

// The plate board grammar (Task #352 — primary sources on the board; Python
// mirror in lesson_anchors). A plate shows a CURATED primary-source image:
//   {"kind": "plate", "asset": "<legend>/<asset-id>", "focus":
//    [{"region": "<named-rect>", "at": <anchor>}], …}
// Assets resolve ONLY against the per-legend signed manifest
// (static/plates/<legend>/manifest.json — plate_manifest.py / plates.mjs);
// focus regions are NAMED RECTS defined once at curation — authors never
// type coordinates or URLs (Law 2). "full" is reserved for the whole plate.
export const PLATE_BOARD_KEYS = ["kind", "asset", "title", "caption", "focus",
  "at", "fx", "id", "marks"];
const PLATE_ASSET_RE = /^[a-z0-9][a-z0-9-]{0,47}\/[a-z0-9][a-z0-9-]{0,47}$/;
const PLATE_REGION_RE = /^[a-z0-9][a-z0-9-]{0,47}$/;

// The hold beat grammar (Task #353 — interaction density; Python mirror in
// lesson_anchors). A hold is the UNGRADED predict-pause: the legend poses a
// question ("What happens to the area?"), the student commits a prediction,
// then the reveal lands. The shape is CLOSED:
//   {"kind": "hold", "prompt": "…", "options": ["…", …],
//    "reveal": {"text": "…", "say": "…"?}, "say": "…"?, "id"?, "direction"?}
// Strings only inside prompt/options/reveal — no widgets, no boards, no
// nested interactions (holds cannot nest), no grading (predictions are never
// punished). The reveal is SERVER-HELD at serve time (Trap 5: fetched on
// commit, never shipped), so authored content REQUIRES it while served
// content never carries it.
export const HOLD_BEAT_KEYS = ["kind", "id", "say", "prompt", "options",
  "reveal", "direction"];
export const HOLD_REVEAL_KEYS = ["text", "say"];
// Nested-interaction tripwires: any of these on a hold beat = an interaction
// inside an interaction (or a graded hold) — the one thing a hold must never be.
const HOLD_NEST_KEYS = ["widget", "problem", "game", "board", "skillCheck",
  "checkpoint", "hold"];
const HOLD_OPTIONS_MIN = 2;
const HOLD_OPTIONS_MAX = 5;
const HOLD_OPTION_MAX_LEN = 80;
const HOLD_PROMPT_MAX_LEN = 200;
const HOLD_REVEAL_TEXT_MAX_LEN = 400;

// The custom-widget beat grammar (v9 — the authored-widget escape hatch;
// Python mirror in lesson_anchors). A custom beat carries a COMPLETE
// self-contained little web page (html/css/js — full authoring flexibility)
// that the stage performs EXCLUSIVELY inside a null-origin sandboxed iframe
// (sandbox="allow-scripts", NEVER allow-same-origin): the widget can never
// touch the app's DOM, cookies, session, storage, or credentialed APIs.
// On exactly that guarantee, the `html` payload is the ONE string the
// store's banned-pattern lint exempts; every OTHER field lints like any
// other string. Shape is CLOSED:
//   {"kind": "custom", "html": "…", "say"?, "title"?, "height"?,
//    "gate"?: "none"|"complete", "fallback"?, "id"?, "direction"?}
export const CUSTOM_BEAT_KEYS = ["kind", "id", "say", "title", "html",
  "height", "gate", "fallback", "direction", "role", "xp", "bubble"];
export const CUSTOM_GATE_VALUES = ["none", "complete"];
export const CUSTOM_ROLE_VALUES = ["celebration"];
export const CUSTOM_BUBBLE_VALUES = ["none"];
const WIDGET_EVENT_RE = /^[A-Za-z_][A-Za-z0-9_-]{0,31}$/;
const CUSTOM_NEST_KEYS = ["widget", "problem", "game", "board", "skillCheck",
  "checkpoint", "hold"];
const CUSTOM_HTML_MAX_LEN = 100000;
const CUSTOM_TITLE_MAX_LEN = 140;
const CUSTOM_FALLBACK_MAX_LEN = 400;
const CUSTOM_HEIGHT_MIN = 240;
const CUSTOM_HEIGHT_MAX = 1600;

// The memory grammar (Task #354 — echo + pin + student tokens; Python mirror
// in lesson_anchors). Three primitives, all closed:
//
// `echo` — per-distractor feedback on a GRADED check beat:
//   {"echo": {"<option index>": {"say": "…", "text": "…"?,
//                                "remedy": "<remedy id>"?}, …}}
// Allowed ONLY on skill-check problem beats whose widget grades an option
// INDEX (the pick-semantics set below) — that index is the distractor key.
// The mapping is SERVER-HELD (Trap 5): stripped at serve, delivered only on
// a wrong commit via /api/learn/grade. `say` narration is pre-generated at
// publish (TTS can never interpolate at play).
//
// `remedies` — a LESSON-LEVEL pool of depth-1 remedy beats echo entries
// reference by id: {"remedies": {"<id>": {"say": "…", "board"?, "direction"?,
// "id"?}}}. The key set is CLOSED and excludes echo/remedy/widget/interaction
// keys, so remedy chains and cycles are impossible by construction (a DAG of
// depth 1 — several distractors may share one remedy).
//
// `pin` — the margin rail: {"pin": [{"op": "add", "id": "…", "text": "…"} |
// {"op": "erase"|"dim", "id": "…"}, …]} on non-hold beats. At most 6 live
// facts, text-only, append/erase/dim ONLY (no layout ops); facts accumulate
// across beats and assemble the end-of-lesson summary.
export const ECHO_WIDGET_TYPES = ["pick", "graphTrace", "curveTrace", "regionPick"];
export const ECHO_ENTRY_KEYS = ["say", "text", "remedy"];
const ECHO_TEXT_MAX_LEN = 200;
export const REMEDY_BEAT_KEYS = ["say", "board", "direction", "id"];
// Depth-1 tripwires: any of these on a remedy beat = a chain, a cycle, or an
// interaction inside the detour — the things a remedy must never be.
const REMEDY_NEST_KEYS = ["echo", "remedy", "widget", "problem", "game", "hold",
  "skillCheck", "checkpoint", "pin", "prompt", "options", "reveal", "kind"];
export const PIN_OPS = ["add", "erase", "dim"];
export const PIN_MAX = 6;
const PIN_TEXT_MAX_LEN = 120;

// Student tokens (Task #354): "{{student.<name>|<fallback>}}" — within-session
// values substituted into BOARD TEXT at render. Closed namespace, mandatory
// fallback, escaped by the renderer, and NEVER in narration (`say` fields) —
// pre-rendered TTS cannot interpolate. Values die with the session (no
// cross-lesson personalization — DO NOT BUILD).
export const STUDENT_TOKEN_NAMES = ["answer", "prediction"];
const STUDENT_TOKEN_RE = /\{\{student\.([a-z_]+)\|([^{}|]+)\}\}/g;
const STUDENT_TOKEN_ATTEMPT = "{{student.";

/** Runtime student-token substitution (Task #354). `values` is the SESSION-
 * ONLY map ({answer, prediction}) owned by the player — tokens die with the
 * session; nothing is persisted or read back across lessons (DO NOT BUILD).
 * Returns PLAIN TEXT: the caller escapes the result before it reaches
 * innerHTML (substitute-then-escape, so a value can never smuggle markup).
 * Missing/blank values take the authored fallback — mandatory by grammar. */
export function substituteStudentTokens(text, values) {
  const s = text == null ? "" : String(text);
  if (s.indexOf(STUDENT_TOKEN_ATTEMPT) === -1) return s;
  return s.replace(STUDENT_TOKEN_RE, (full, name, fallback) => {
    const v = values && Object.prototype.hasOwnProperty.call(values, name)
      ? values[name] : undefined;
    return (v === undefined || v === null || String(v).trim() === "")
      ? fallback : String(v);
  });
}

// The closed reveal-effect enum (Task #348). Authors pick HOW a fragment
// arrives — write (chalk wipe), fade, draw (figure wipe), none — and nothing
// else: no authored durations, colors, or sounds (runtime owns the styling
// and the chalk foley). Anything outside this enum is a validation error.
export const REVEAL_FX = new Set(["write", "fade", "draw", "none"]);

// The closed mark-style enum (Task #350 — the lecturer's hand). A mark is
// inline emphasis on a DECLARED target: {"mark": style, "target": "<verbatim
// substring of one board text slot>", "at": <anchor>}. The palette is FIXED
// and runtime-owned (Law 2: authors declare data, never colors/coordinates):
//   hl   — the circled word (chalk ellipse)
//   term — a term being defined (chalk underline, ink accent)
//   warn — caution (amber underline)
//   dim  — de-emphasis (the text recedes)
export const MARK_STYLES = new Set(["hl", "term", "warn", "dim"]);

// The closed direction-verb enum (Task #351 — the derived performance). The
// runtime DERIVES the legend's gaze/gesture from board events by default;
// `beat.direction` is the small authored override slot for the ~5% of moments
// an author wants to direct: [{"verb": <one of these>, "at": <anchor>}].
// Head/body channels ONLY — A2F owns the face during speech and lip-sync is
// sacred; the runtime owns amplitude, timing and rate caps (Law 2). Anything
// outside this enum is a validation error.
export const DIRECTION_VERBS = new Set([
  "gaze_board",   // turn toward the board
  "glance_board", // quick flick toward the board
  "gaze_student", // face the student (attention lift toward camera)
  "point_board",  // chin-point/indicate the board
  "scan_board",   // slow sweep across the board and back
  "lean_in",      // attentive lean toward the student
  "tilt",         // curious head tilt
  "nod",          // affirm
  "shake",        // gentle "not quite"
  "consider",     // thinking beat
  "celebrate",    // warm, unmistakable delight
  "settle",       // settle to calm — suppress derived gestures for a beat
]);

// ─────────────────────────────────────────────────────────────────────────────
// Anchor grammar (JS mirror of lesson_anchors.classify_anchor — keep in step)
// ─────────────────────────────────────────────────────────────────────────────

const TIMEY_KEYS = new Set([
  "ms", "time", "t", "sec", "secs", "seconds", "minute", "minutes",
  "timems", "time_ms", "atms", "at_ms", "abs", "absolute", "offset",
  "offsetms", "offset_ms", "start", "end",
]);
const AFTER_ID_RE = /^[A-Za-z_][A-Za-z0-9_-]{0,31}$/;
const ALIGN_RE = /^[0-9a-f]{8,64}$/;
// widget/problem carry their own schemas (graded content, not performance
// timing). `direction` (Task #351) IS cue grammar now: its `at` anchors are
// collected, linted, resolved and armed like every other cue — no absolute
// times (Law 1: narration is the only clock).
const EXCLUDED_SUBTREES = ["widget", "problem"];

export function classifyAnchor(at) {
  const bad = (msgs) => ({ kind: "invalid", errors: Array.isArray(msgs) ? msgs : [msgs] });
  if (!at || typeof at !== "object" || Array.isArray(at)) {
    return bad(
      '`at` must be an object — authored absolute times are forbidden (Law 1: ' +
      'narration is the only clock). Use {"word": <token index>}, {"word": "<word>"} ' +
      '(authoring sugar), {"after": "<board event id>"} or {"event": "<widget event>"}.');
  }
  const keys = Object.keys(at);
  const timey = keys.filter((k) => k !== "tMs" && TIMEY_KEYS.has(String(k).toLowerCase())).sort();
  if (timey.length) {
    return bad(`\`at\` carries time field(s) [${timey.join(", ")}] — authored absolute times ` +
      "are forbidden (Law 1: narration is the only clock).");
  }
  if (keys.includes("after")) {
    if (keys.length !== 1) return bad(`\`at\` with "after" allows no other keys (got ${keys.sort().join(",")})`);
    const a = at.after;
    if (typeof a !== "string" || !AFTER_ID_RE.test(a)) {
      return bad(`\`at.after\` must be a board-event id matching ${AFTER_ID_RE.source} (got ${JSON.stringify(a)})`);
    }
    return { kind: "after", errors: [] };
  }
  if (keys.includes("event")) {
    if (keys.length !== 1) return bad(`\`at\` with "event" allows no other keys (got ${keys.sort().join(",")})`);
    const event = at.event;
    if (typeof event !== "string" || !WIDGET_EVENT_RE.test(event)) {
      return bad(`\`at.event\` must be a widget-event name matching ${WIDGET_EVENT_RE.source} (got ${JSON.stringify(event)})`);
    }
    if (["ready", "resize", "progress", "complete"].includes(event)) {
      return bad(`\`at.event\` ${JSON.stringify(event)} is reserved by the widget bridge`);
    }
    return { kind: "widget-event", errors: [] };
  }
  if (keys.includes("word")) {
    const extra = keys.filter((k) => !["word", "tMs", "align"].includes(k));
    if (extra.length) return bad(`\`at\` allows only word/tMs/align keys (got extra ${extra.sort().join(",")})`);
    const w = at.word;
    const hasT = keys.includes("tMs");
    const hasA = keys.includes("align");
    if (hasT !== hasA) return bad("`at.tMs` and `at.align` must appear together (resolver-written pair)");
    if (typeof w === "boolean") return bad("`at.word` must be a token index (int) or a word string, not a boolean");
    if (typeof w === "number") {
      if (!Number.isInteger(w) || w < 0) return bad(`\`at.word\` token index must be an integer >= 0 (got ${w})`);
      if (hasT) {
        const errs = [];
        if (typeof at.tMs !== "number" || !Number.isInteger(at.tMs) || at.tMs < 0) {
          errs.push(`\`at.tMs\` must be a non-negative integer ms (got ${JSON.stringify(at.tMs)})`);
        }
        if (typeof at.align !== "string" || !ALIGN_RE.test(at.align)) {
          errs.push(`\`at.align\` must be an alignment hash (8–64 hex chars, got ${JSON.stringify(at.align)})`);
        }
        return errs.length ? bad(errs) : { kind: "word-index", errors: [] };
      }
      return { kind: "word-index", errors: [] };
    }
    if (typeof w === "string") {
      if (!w.trim() || w.length > 80) return bad("`at.word` sugar must be a non-empty word (<= 80 chars)");
      if (hasT) {
        return bad("`at.word` sugar cannot carry tMs/align — sugar resolves to a token index " +
          "at publish; only resolved index anchors are pinned");
      }
      return { kind: "sugar", errors: [] };
    }
    return bad(`\`at.word\` must be an int token index or a word string (got ${typeof w})`);
  }
  return bad(`unknown \`at\` shape (keys ${keys.sort().join(",")}) — the grammar is closed: ` +
    '{"word": int}, {"word": "str"} (publish-time sugar), {"after": "id"} or {"event": "name"}');
}

export function isResolvedAnchor(at) {
  const { kind, errors } = classifyAnchor(at);
  if (errors.length) return false;
  if (kind === "after" || kind === "widget-event") return true;
  return kind === "word-index" && "tMs" in at && "align" in at;
}

/** Deep-walk a beat for anchor containers, mirroring lesson_anchors.iter_anchors:
 * every dict carrying an `at` key, excluding top-level widget/problem subtrees.
 * Paths are stable so runtime, validator and server name the same cue. */
export function collectAnchors(beat) {
  const found = [];
  const walk = (node, pathStr, depth0Key) => {
    if (Array.isArray(node)) {
      node.forEach((v, i) => walk(v, `${pathStr}[${i}]`, null));
      return;
    }
    if (!node || typeof node !== "object") return;
    if ("at" in node) found.push({ path: pathStr, container: node });
    for (const [k, v] of Object.entries(node)) {
      if (k === "at") continue;
      if (pathStr === "" && EXCLUDED_SUBTREES.includes(k)) continue;
      walk(v, pathStr ? `${pathStr}.${k}` : k, null);
    }
  };
  if (beat && typeof beat === "object" && !Array.isArray(beat)) walk(beat, "", null);
  return found;
}

/** Deep-walk a beat for reveal-grammar containers — every dict carrying `fx`
 * (and/or `id` alongside `at`/`fx`), same exclusion rules as collectAnchors.
 * Mirror of lesson_anchors.iter_fx — keep in step. */
export function collectFxContainers(beat) {
  const found = [];
  const walk = (node, pathStr, inBoard) => {
    if (Array.isArray(node)) {
      node.forEach((v, i) => walk(v, `${pathStr}[${i}]`, inBoard));
      return;
    }
    if (!node || typeof node !== "object") return;
    // Reveal-grammar containers: any `fx`; an `id` anywhere INSIDE the board
    // subtree (bare ids are after-event SOURCES there), or an `id` riding an
    // `at` elsewhere. A beat's own `id` (game/lesson keys) is NOT grammar.
    if ("fx" in node || (("id" in node) && (inBoard || "at" in node))) {
      found.push({ path: pathStr, container: node });
    }
    for (const [k, v] of Object.entries(node)) {
      if (k === "at") continue;
      if (pathStr === "" && EXCLUDED_SUBTREES.includes(k)) continue;
      walk(v, pathStr ? `${pathStr}.${k}` : k, inBoard || (pathStr === "" && k === "board"));
    }
  };
  if (beat && typeof beat === "object" && !Array.isArray(beat)) walk(beat, "", false);
  return found;
}

/** Enumerate a board's TEXT slots in render order — the address space mark
 * targets resolve against (Task #350). Environment-free twin of the slots
 * LessonBoardRenderer.boardHTML prints as text; the Python mirror is
 * lesson_anchors.board_mark_texts — keep all three in lockstep. `latex` is
 * the equation SOURCE string (marks may reach KaTeX sub-expressions by
 * addressing a verbatim substring of the source). */
export function boardMarkTexts(board) {
  if (!board || typeof board !== "object" || Array.isArray(board)) return [];
  const out = [];
  const text = (v) => {
    if (v && typeof v === "object" && !Array.isArray(v)) return v.text == null ? "" : String(v.text);
    return v == null ? "" : String(v);
  };
  const push = (slot, v) => { const t = text(v); if (t) out.push({ slot, text: t }); };
  const k = board.kind;
  if (k === "heading") {
    push("kicker", board.kicker);
    push("title", board.title);
    (Array.isArray(board.lines) ? board.lines : []).forEach((l, i) => push(`line${i}`, l));
  } else if (k === "equation") {
    push("title", board.title);
    push("latex", board.latex);
    push("caption", board.caption);
  } else if (k === "visual") {
    push("title", board.title);
    push("caption", board.caption);
  } else if (k === "definition") {
    push("term", board.term);
    push("meaning", board.meaning);
  } else if (k === "diagram") {
    push("title", board.title);
    (Array.isArray(board.steps) ? board.steps : []).forEach((s, i) => push(`step${i}`, s));
  } else if (k === "completion") {
    push("kicker", board.kicker);
    push("title", board.title);
  } else if (k === "leagues") {
    push("title", board.title);
  } else if (k === "plate") {
    // Only text the block DECLARES is addressable (Law 2) — the manifest
    // caption shown when the author omits one is curation data, not a
    // mark target.
    push("title", board.title);
    push("caption", board.caption);
  } else {
    // points / example / summary → the generic lines renderer
    push("title", board.title);
    (Array.isArray(board.lines) ? board.lines : []).forEach((l, i) => push(`line${i}`, l));
  }
  return out;
}

/** Lint one board's `marks` array (Task #350). The grammar is CLOSED:
 * [{mark: MARK_STYLES, target: "<verbatim substring of ONE text slot>",
 *   at: <anchor>}]. Target addressing is case-sensitive substring match over
 * boardMarkTexts; a target found in zero slots or in more than one slot is
 * an authoring error (marks emphasize text the block DECLARES — Law 2), and
 * two marks whose ranges overlap in the same slot can never both render.
 * `at` is REQUIRED (an unanchored mark could never fire — Law 1: the hand
 * moves on the narration); its SHAPE is linted by the anchor walk, which
 * collects every dict carrying `at`. Python mirror: lesson_anchors. */
function lintBoardMarks(board, locBase, errors, warnings) {
  const marks = board && typeof board === "object" && !Array.isArray(board)
    ? board.marks : undefined;
  if (marks === undefined) return 0;
  const locB = `${locBase}.board.marks`;
  if (!Array.isArray(marks)) {
    errors.push(`${locB}: must be an array of {mark, target, at} objects`);
    return 1;
  }
  const texts = boardMarkTexts(board);
  const taken = new Map(); // slot → [[start, end, mi]] for overlap detection
  let count = 0;
  marks.forEach((m, mi) => {
    count += 1;
    const loc = `${locB}[${mi}]`;
    if (!m || typeof m !== "object" || Array.isArray(m)) {
      errors.push(`${loc}: must be an object {mark, target, at}`);
      return;
    }
    const extra = Object.keys(m).filter((kk) => !["mark", "target", "at"].includes(kk));
    if (extra.length) {
      errors.push(`${loc}: allows only mark/target/at keys (got extra ${extra.sort().join(",")}) — ` +
        "the mark grammar is closed");
    }
    if (typeof m.mark !== "string" || !MARK_STYLES.has(m.mark)) {
      errors.push(`${loc}.mark: must be one of ${[...MARK_STYLES].map((s) => JSON.stringify(s)).join("|")} ` +
        `(got ${JSON.stringify(m.mark)}) — the style enum is closed; the palette is runtime-owned (Law 2)`);
    }
    const t = m.target;
    if (typeof t !== "string" || !t.trim() || t.length > 120) {
      errors.push(`${loc}.target: must be a non-empty string (<= 120 chars) copied verbatim ` +
        "from ONE board text slot");
    } else {
      const hits = texts.filter((s) => s.text.includes(t));
      if (!hits.length) {
        errors.push(`${loc}.target: ${JSON.stringify(t)} not found in any board text slot — ` +
          "marks may only emphasize text the block declares");
      } else if (hits.length > 1) {
        errors.push(`${loc}.target: ${JSON.stringify(t)} is ambiguous — found in ${hits.length} ` +
          `slots (${hits.map((h) => h.slot).join(", ")}); use a longer verbatim substring`);
      } else {
        const occ = hits[0].text.split(t).length - 1;
        if (occ > 1) {
          warnings.push(`${loc}.target: ${JSON.stringify(t)} occurs ${occ} times in slot ` +
            `${hits[0].slot} — the FIRST occurrence is marked`);
        }
        const start = hits[0].text.indexOf(t);
        const end = start + t.length;
        const ranges = taken.get(hits[0].slot) || [];
        const clash = ranges.find(([s, e]) => start < e && end > s);
        if (clash) {
          errors.push(`${loc}.target: ${JSON.stringify(t)} overlaps mark #${clash[2]}'s target ` +
            `in slot ${hits[0].slot} — mark ranges must be disjoint`);
        } else {
          ranges.push([start, end, mi]);
          taken.set(hits[0].slot, ranges);
        }
      }
    }
    if (!("at" in m)) {
      errors.push(`${loc}: mark has no \`at\` anchor — the hand moves on the narration ` +
        "(Law 1); an unanchored mark can never fire");
    }
  });
  return count;
}

/** Lint one beat's `direction` array (Task #351 — the derived performance).
 * The grammar is CLOSED: [{verb: DIRECTION_VERBS, at: <anchor>}]. The runtime
 * derives the performance from board events by default — this slot is the
 * authored 5%, layered over the defaults. `at` is REQUIRED (Law 1: the
 * performance moves on the narration — no absolute times); its SHAPE is
 * linted by the anchor walk, which collects every dict carrying `at`.
 * Python mirror: lesson_anchors._lint_beat_direction. */
function lintBeatDirection(beat, locBase, errors) {
  const dir = beat && typeof beat === "object" && !Array.isArray(beat)
    ? beat.direction : undefined;
  if (dir === undefined) return 0;
  const locD = `${locBase}.direction`;
  if (!Array.isArray(dir)) {
    errors.push(`${locD}: must be an array of {verb, at} objects — the direction ` +
      "grammar is closed (the runtime derives the performance; this slot only overrides)");
    return 1;
  }
  let count = 0;
  dir.forEach((entry, di) => {
    count += 1;
    const loc = `${locD}[${di}]`;
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      errors.push(`${loc}: must be an object {verb, at}`);
      return;
    }
    const extra = Object.keys(entry).filter((kk) => !["verb", "at"].includes(kk));
    if (extra.length) {
      errors.push(`${loc}: allows only verb/at keys (got extra ${extra.sort().join(",")}) — ` +
        "the direction grammar is closed; the runtime owns amplitude, timing and rate caps (Law 2)");
    }
    if (typeof entry.verb !== "string" || !DIRECTION_VERBS.has(entry.verb)) {
      errors.push(`${loc}.verb: must be one of ${[...DIRECTION_VERBS].map((s) => JSON.stringify(s)).join("|")} ` +
        `(got ${JSON.stringify(entry.verb)}) — the verb enum is closed`);
    }
    if (!("at" in entry)) {
      errors.push(`${loc}: direction entry has no \`at\` anchor — the performance moves ` +
        "on the narration (Law 1); an unanchored verb can never fire");
    }
  });
  return count;
}

/** Lint one plate board's shape (Task #352 — primary sources on the board).
 * The grammar is CLOSED (PLATE_BOARD_KEYS): the author declares an ASSET ID
 * from the legend's signed manifest and NAMED focus regions — never URLs,
 * coordinates, or zoom levels (Law 2; pan/zoom is runtime behavior). Each
 * focus entry is {region, at} with `at` REQUIRED (Law 1: focus moves on the
 * narration). Manifest EXISTENCE (asset curated, region named, alt present)
 * is filesystem truth checked at publish by plate_manifest.py /
 * scripts/goldframe/plates.mjs — this shape lint is environment-free so the
 * twin fixtures stay runnable everywhere. `_resolved` is the SERVER-stamped
 * resolution payload and must never appear in authored content. Python
 * mirror: lesson_anchors._lint_plate_board. */
function lintPlateBoard(board, locBase, errors) {
  if (!board || typeof board !== "object" || Array.isArray(board) ||
      board.kind !== "plate") return 0;
  const locP = `${locBase}.board`;
  const extra = Object.keys(board).filter((k) => !PLATE_BOARD_KEYS.includes(k));
  const ri = extra.indexOf("_resolved");
  if (ri >= 0) {
    extra.splice(ri, 1);
    errors.push(`${locP}._resolved: server-stamped at serve time — remove it from ` +
      "authored content (assets resolve only against the signed manifest)");
  }
  if (extra.length) {
    errors.push(`${locP}: allows only ${PLATE_BOARD_KEYS.join("/")} keys (got extra ` +
      `${extra.sort().join(",")}) — the plate grammar is closed`);
  }
  const asset = board.asset;
  if (typeof asset !== "string" || !PLATE_ASSET_RE.test(asset)) {
    errors.push(`${locP}.asset: must be a <legend>/<asset-id> ref matching ` +
      `${PLATE_ASSET_RE.source} (got ${JSON.stringify(asset)}) — plates resolve ONLY against ` +
      "the legend's signed manifest; free URLs are forbidden (Law 2)");
  }
  const focus = board.focus;
  if ("focus" in board && !Array.isArray(focus)) {
    errors.push(`${locP}.focus: must be an array of {region, at} objects — focus ` +
      "moves are named rects on narration anchors");
  }
  (Array.isArray(focus) ? focus : []).forEach((entry, fi) => {
    const loc = `${locP}.focus[${fi}]`;
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      errors.push(`${loc}: must be an object {region, at}`);
      return;
    }
    const fExtra = Object.keys(entry).filter((kk) => !["region", "at"].includes(kk));
    if (fExtra.length) {
      errors.push(`${loc}: allows only region/at keys (got extra ${fExtra.sort().join(",")}) — ` +
        "authors name a curated rect; the runtime owns the camera (Law 2)");
    }
    const region = entry.region;
    if (typeof region !== "string" || !PLATE_REGION_RE.test(region)) {
      errors.push(`${loc}.region: must be a curated region name matching ` +
        `${PLATE_REGION_RE.source} (got ${JSON.stringify(region)}) — regions are defined ` +
        'once in the manifest; "full" is the whole plate');
    }
    if (!("at" in entry)) {
      errors.push(`${loc}: focus entry has no \`at\` anchor — focus moves on the ` +
        "narration (Law 1); an unanchored focus can never fire");
    }
  });
  return 1;
}

/** Lint one hold beat's shape (Task #353 — the ungraded predict-pause).
 * The grammar is CLOSED (HOLD_BEAT_KEYS): a prompt, 2–5 short string
 * options, and a server-held reveal. Holds cannot nest — no widget/problem/
 * game/board/skillCheck/checkpoint keys (an interaction inside an
 * interaction), and the reveal is strings-only so nothing can hide inside
 * it. Predictions are never graded, so nothing here carries an answer.
 * Python mirror: lesson_anchors._lint_hold_beat. */
function lintHoldBeat(beat, locBase, errors) {
  if (!beat || typeof beat !== "object" || Array.isArray(beat) ||
      beat.kind !== "hold") return 0;
  const nested = HOLD_NEST_KEYS.filter((k) => k in beat);
  if (nested.length) {
    errors.push(`${locBase}: hold beats cannot nest interactions or carry ` +
      `boards/grading (got ${nested.slice().sort().join(",")}) — a hold is an ` +
      "ungraded predict-pause: prompt, options, commit, reveal");
  }
  const extra = Object.keys(beat).filter(
    (k) => !HOLD_BEAT_KEYS.includes(k) && !HOLD_NEST_KEYS.includes(k));
  if (extra.length) {
    errors.push(`${locBase}: hold allows only ${HOLD_BEAT_KEYS.join("/")} keys ` +
      `(got extra ${extra.sort().join(",")}) — the hold grammar is closed`);
  }
  const prompt = beat.prompt;
  if (typeof prompt !== "string" || !prompt.trim()) {
    errors.push(`${locBase}.prompt: hold requires a non-empty string prompt — ` +
      "the predict question is the whole point of the pause");
  } else if (prompt.length > HOLD_PROMPT_MAX_LEN) {
    errors.push(`${locBase}.prompt: ${prompt.length} chars exceeds the ` +
      `${HOLD_PROMPT_MAX_LEN}-char budget — a predict question is one ` +
      "sharp line, not a paragraph");
  }
  const options = beat.options;
  if (!Array.isArray(options) ||
      options.length < HOLD_OPTIONS_MIN || options.length > HOLD_OPTIONS_MAX) {
    const got = Array.isArray(options) ? options.length : JSON.stringify(options ?? null);
    errors.push(`${locBase}.options: hold requires ${HOLD_OPTIONS_MIN}–` +
      `${HOLD_OPTIONS_MAX} prediction options (got ${got}) — the student ` +
      "commits to one before the reveal");
  } else {
    options.forEach((opt, oi) => {
      if (typeof opt !== "string" || !opt.trim()) {
        errors.push(`${locBase}.options[${oi}]: must be a non-empty string — ` +
          "options are plain text predictions, never objects");
      } else if (opt.length > HOLD_OPTION_MAX_LEN) {
        errors.push(`${locBase}.options[${oi}]: ${opt.length} chars exceeds the ` +
          `${HOLD_OPTION_MAX_LEN}-char option budget`);
      }
    });
  }
  const reveal = beat.reveal;
  if (reveal === undefined || reveal === null) {
    // Trap 5: the reveal is popped at SERVE time (fetch-on-commit), so
    // authored/stored/published lessons must always carry it — a hold with
    // no reveal has no payoff. (Served payloads are never linted.)
    errors.push(`${locBase}.reveal: hold requires a reveal ` +
      "{text, say?} — a prediction with no payoff is a dead pause " +
      "(it is stripped at serve and fetched on commit, Trap 5)");
  } else if (typeof reveal !== "object" || Array.isArray(reveal)) {
    errors.push(`${locBase}.reveal: must be an object {text, say?} — strings ` +
      "only; the reveal stays server-held until commit (Trap 5)");
  } else {
    const rNested = Object.keys(reveal).filter((k) =>
      HOLD_NEST_KEYS.includes(k) || ["prompt", "options", "reveal"].includes(k));
    if (rNested.length) {
      errors.push(`${locBase}.reveal: holds cannot nest (got ` +
        `${rNested.slice().sort().join(",")} inside the reveal) — a reveal is ` +
        "text the moment lands on, not another interaction");
    }
    const rExtra = Object.keys(reveal).filter(
      (k) => !HOLD_REVEAL_KEYS.includes(k) && !rNested.includes(k));
    if (rExtra.length) {
      errors.push(`${locBase}.reveal: allows only ${HOLD_REVEAL_KEYS.join("/")} ` +
        `keys (got extra ${rExtra.sort().join(",")}) — the reveal grammar is closed`);
    }
    const text = reveal.text;
    if (typeof text !== "string" || !text.trim()) {
      errors.push(`${locBase}.reveal.text: hold reveal requires a non-empty ` +
        "string — it is what the student committed to see");
    } else if (text.length > HOLD_REVEAL_TEXT_MAX_LEN) {
      errors.push(`${locBase}.reveal.text: ${text.length} chars exceeds the ` +
        `${HOLD_REVEAL_TEXT_MAX_LEN}-char budget`);
    }
    const say = reveal.say;
    if (say !== undefined && say !== null &&
        (typeof say !== "string" || !say.trim())) {
      errors.push(`${locBase}.reveal.say: when present, must be a non-empty ` +
        "string — the spoken payoff line");
    }
  }
  return 1;
}

/** Lint one custom-widget beat's shape (v9 — the authored-widget escape
 * hatch). The grammar is CLOSED (CUSTOM_BEAT_KEYS): a self-contained html
 * payload the stage performs ONLY inside a null-origin sandboxed iframe,
 * plus presentation knobs (title/height/gate/fallback). Custom beats cannot
 * nest — no widget/problem/game/board/skillCheck/checkpoint/hold keys (one
 * activity per beat). The html string itself is deliberately NOT
 * content-linted (it is full HTML/JS by design); the SANDBOX is the
 * boundary, not the lint. Python mirror: lesson_anchors._lint_custom_beat. */
export function lintCustomBeat(beat, locBase, errors) {
  if (!beat || typeof beat !== "object" || Array.isArray(beat) ||
      beat.kind !== "custom") return 0;
  const nested = CUSTOM_NEST_KEYS.filter((k) => k in beat);
  if (nested.length) {
    errors.push(`${locBase}: custom beats cannot nest interactions or carry ` +
      `boards (got ${nested.slice().sort().join(",")}) — a custom beat is ONE ` +
      "self-contained sandboxed activity");
  }
  const extra = Object.keys(beat).filter(
    (k) => !CUSTOM_BEAT_KEYS.includes(k) && !CUSTOM_NEST_KEYS.includes(k));
  if (extra.length) {
    errors.push(`${locBase}: custom allows only ${CUSTOM_BEAT_KEYS.join("/")} ` +
      `keys (got extra ${extra.sort().join(",")}) — the custom-widget grammar ` +
      "is closed");
  }
  const html = beat.html;
  if (typeof html !== "string" || !html.trim()) {
    errors.push(`${locBase}.html: custom requires a non-empty html string — ` +
      "the widget IS the beat (it runs sandboxed; LESSON_SCHEMA.md §4.5)");
  } else if (html.length > CUSTOM_HTML_MAX_LEN) {
    errors.push(`${locBase}.html: ${html.length} chars exceeds the ` +
      `${CUSTOM_HTML_MAX_LEN}-char widget budget — inline everything, but ` +
      "keep the page lean (no bundlers, no CDNs at play time)");
  }
  const title = beat.title;
  if (title !== undefined && title !== null &&
      (typeof title !== "string" || title.length > CUSTOM_TITLE_MAX_LEN)) {
    errors.push(`${locBase}.title: when present, a string of at most ` +
      `${CUSTOM_TITLE_MAX_LEN} chars`);
  }
  const gate = beat.gate;
  if (gate !== undefined && gate !== null && !CUSTOM_GATE_VALUES.includes(gate)) {
    errors.push(`${locBase}.gate: must be one of ${CUSTOM_GATE_VALUES.join("/")} ` +
      `(got ${JSON.stringify(gate)}) — "complete" locks Continue until the ` +
      "widget posts its completion message");
  }
  const height = beat.height;
  if (height !== undefined && height !== null &&
      (typeof height !== "number" || !Number.isInteger(height) ||
       height < CUSTOM_HEIGHT_MIN || height > CUSTOM_HEIGHT_MAX)) {
    errors.push(`${locBase}.height: when present, an integer px height ` +
      `${CUSTOM_HEIGHT_MIN}..${CUSTOM_HEIGHT_MAX} (widgets may also resize ` +
      "live over the bridge)");
  }
  const fb = beat.fallback;
  if (fb !== undefined && fb !== null &&
      (typeof fb !== "string" || !fb.trim() || fb.length > CUSTOM_FALLBACK_MAX_LEN)) {
    errors.push(`${locBase}.fallback: when present, a non-empty string of at ` +
      `most ${CUSTOM_FALLBACK_MAX_LEN} chars — shown if the widget never ` +
      "boots (never-strand)");
  }
  if (beat.role !== undefined && !CUSTOM_ROLE_VALUES.includes(beat.role)) {
    errors.push(`${locBase}.role: when present, must be "celebration"`);
  }
  if (beat.xp !== undefined && beat.xp !== 0) {
    errors.push(`${locBase}.xp: custom beat xp may only be 0 (no-reward ceremony)`);
  }
  if (beat.bubble !== undefined && !CUSTOM_BUBBLE_VALUES.includes(beat.bubble)) {
    errors.push(`${locBase}.bubble: when present, must be "none"`);
  }
  return 1;
}

const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);

/** Lint one beat's `echo` map (Task #354 — per-distractor feedback).
 * Allowed ONLY on skill-check problem beats whose widget grades an option
 * INDEX (ECHO_WIDGET_TYPES) — the submitted index is the distractor key.
 * Keys are canonical decimal strings, in option range, never the correct
 * answer. Entries are closed {say, text?, remedy?}; remedy refs must exist
 * in the lesson-level pool. SERVER-HELD (Trap 5): stripped at serve,
 * fetched on a wrong commit. Python mirror: lesson_anchors._lint_echo_beat. */
export function lintEchoBeat(beat, remedies, locBase, errors) {
  if (!isObj(beat) || !("echo" in beat)) return 0;
  const echo = beat.echo;
  const w = beat.widget;
  const wtype = isObj(w) ? w.type : undefined;
  const locE = `${locBase}.echo`;
  if (beat.kind !== "problem" || !beat.skillCheck ||
      typeof wtype !== "string" || !ECHO_WIDGET_TYPES.includes(wtype)) {
    errors.push(`${locE}: echo is allowed only on skill-check problem beats with ` +
      `a ${ECHO_WIDGET_TYPES.join("/")} widget — per-distractor feedback ` +
      "keys on the graded option index");
    return 1;
  }
  if (!isObj(echo) || !Object.keys(echo).length) {
    errors.push(`${locE}: must be a non-empty object mapping distractor option ` +
      "indices (decimal strings) to {say, text?, remedy?} entries");
    return 1;
  }
  const opts = wtype === "regionPick" ? w.regions : w.options;
  const optN = Array.isArray(opts) ? opts.length : null;
  const answer = w.answer;
  const remedyIds = isObj(remedies) ? new Set(Object.keys(remedies)) : new Set();
  for (const [k, entry] of Object.entries(echo)) {
    const ks = String(k);
    const loc = `${locE}.${ks}`;
    if (!/^[0-9]+$/.test(ks) || String(parseInt(ks, 10)) !== ks) {
      errors.push(`${loc}: echo keys are canonical decimal option indices ` +
        `("0", "1", …) — got "${ks}"`);
      continue;
    }
    const ki = parseInt(ks, 10);
    if (optN !== null && ki >= optN) {
      errors.push(`${loc}: option index ${ki} is out of range — the widget has ` +
        `${optN} options`);
    }
    if (typeof answer === "number" && Number.isInteger(answer) && ki === answer) {
      errors.push(`${loc}: echo maps DISTRACTORS — index ${ki} is the correct ` +
        "answer and can never miss");
    }
    if (!isObj(entry)) {
      errors.push(`${loc}: must be an object {say, text?, remedy?}`);
      continue;
    }
    const extra = Object.keys(entry).filter((kk) => !ECHO_ENTRY_KEYS.includes(kk));
    if (extra.length) {
      errors.push(`${loc}: allows only ${ECHO_ENTRY_KEYS.join("/")} keys (got ` +
        `extra ${extra.sort().join(",")}) — the echo grammar is closed`);
    }
    const say = entry.say;
    if (typeof say !== "string" || !say.trim()) {
      errors.push(`${loc}.say: echo requires a non-empty spoken line — feedback ` +
        "narration is pre-generated at publish and can never interpolate at play");
    }
    const text = entry.text;
    if (text !== undefined && text !== null && typeof text !== "string") {
      errors.push(`${loc}.text: when present, must be a string`);
    } else if (typeof text === "string" && text.length > ECHO_TEXT_MAX_LEN) {
      errors.push(`${loc}.text: ${text.length} chars exceeds the ` +
        `${ECHO_TEXT_MAX_LEN}-char budget — feedback is one sharp line, not a paragraph`);
    }
    const rem = entry.remedy;
    if (rem !== undefined && rem !== null) {
      if (typeof rem !== "string" || !AFTER_ID_RE.test(rem)) {
        errors.push(`${loc}.remedy: must be a remedy id matching ` +
          `${AFTER_ID_RE.source} (got ${JSON.stringify(rem)})`);
      } else if (!remedyIds.has(rem)) {
        errors.push(`${loc}.remedy: references "${rem}" but lesson.remedies ` +
          "has no such entry — a missing remedy is a dead end on a wrong answer");
      }
    }
  }
  return 1;
}

/** Lint the lesson-level `remedies` pool (Task #354). Remedy beats are
 * depth-1 detours: closed keys, no interactions, no echo/remedy of their own
 * — chains and cycles are impossible by construction (the echo→remedy graph
 * is a DAG of depth 1). Python mirror: lesson_anchors._lint_lesson_remedies. */
export function lintLessonRemedies(lesson, where, errors, warnings) {
  if (!isObj(lesson) || !("remedies" in lesson)) return 0;
  const remedies = lesson.remedies;
  const locR = `${where}.remedies`;
  if (!isObj(remedies)) {
    errors.push(`${locR}: must be an object mapping remedy ids to depth-1 remedy ` +
      "beats ({say, board?, direction?, id?})");
    return 1;
  }
  const referenced = new Set();
  const beats = Array.isArray(lesson.beats) ? lesson.beats : [];
  for (const beat of beats) {
    const echo = isObj(beat) ? beat.echo : undefined;
    if (isObj(echo)) {
      for (const entry of Object.values(echo)) {
        if (isObj(entry) && typeof entry.remedy === "string") referenced.add(entry.remedy);
      }
    }
  }
  let count = 0;
  for (const [rid, r] of Object.entries(remedies)) {
    count += 1;
    const loc = `${locR}.${rid}`;
    const okId = typeof rid === "string" && AFTER_ID_RE.test(rid);
    if (!okId) {
      errors.push(`${loc}: remedy id must match ${AFTER_ID_RE.source}`);
    }
    if (!isObj(r)) {
      errors.push(`${loc}: must be an object {say, board?, direction?, id?}`);
      continue;
    }
    const nested = REMEDY_NEST_KEYS.filter((k) => k in r);
    if (nested.length) {
      errors.push(`${loc}: remedy beats cannot chain further (got ` +
        `${nested.slice().sort().join(",")}) — remedies are depth 1: no echo, ` +
        "no remedy, no interactions (the DAG stops here)");
    }
    const extra = Object.keys(r).filter(
      (k) => !REMEDY_BEAT_KEYS.includes(k) && !REMEDY_NEST_KEYS.includes(k));
    if (extra.length) {
      errors.push(`${loc}: allows only ${REMEDY_BEAT_KEYS.join("/")} keys (got ` +
        `extra ${extra.sort().join(",")}) — the remedy grammar is closed`);
    }
    const say = r.say;
    if (typeof say !== "string" || !say.trim()) {
      errors.push(`${loc}.say: remedy requires a non-empty spoken line — it is ` +
        "pre-generated with the lesson and performs like any beat");
    }
    if (okId && !referenced.has(rid)) {
      warnings.push(`${loc}: remedy "${rid}" is never referenced by any echo ` +
        "entry — dead content");
    }
  }
  return count;
}

/** Lint every beat's `pin` ops as ONE sequential projection (Task #354).
 * Append/erase/dim ONLY, at most PIN_MAX live facts, text-only. Hold beats
 * cannot carry `pin` (their closed-key lint owns that error). Python
 * mirror: lesson_anchors._lint_lesson_pins. */
export function lintLessonPins(lesson, where, errors, warnings) {
  const beats = isObj(lesson) && Array.isArray(lesson.beats) ? lesson.beats : null;
  if (!beats) return 0;
  const live = new Map(); // id -> "live" | "dim"
  let count = 0;
  beats.forEach((beat, bi) => {
    if (!isObj(beat) || !("pin" in beat)) return;
    if (beat.kind === "hold" || beat.kind === "custom") return; // their closed-key lints already reject `pin`
    count += 1;
    const ops = beat.pin;
    const locB = `${where}.beats[${bi}].pin`;
    if (!Array.isArray(ops) || !ops.length) {
      errors.push(`${locB}: must be a non-empty array of rail ops ` +
        "({op, id, text?}) — append/erase/dim only, no layout ops");
      return;
    }
    ops.forEach((op, oi) => {
      const loc = `${locB}[${oi}]`;
      if (!isObj(op)) {
        errors.push(`${loc}: must be an object {op, id, text?}`);
        return;
      }
      const o = op.op;
      if (typeof o !== "string" || !PIN_OPS.includes(o)) {
        const enumS = PIN_OPS.map((s) => JSON.stringify(s)).join("|");
        const got = typeof o === "string" ? JSON.stringify(o) : JSON.stringify(o ?? null);
        errors.push(`${loc}.op: must be one of ${enumS} (got ${got}) — the rail ` +
          "is append/erase/dim ONLY; layout is runtime-owned");
        return;
      }
      const pid = op.id;
      if (typeof pid !== "string" || !AFTER_ID_RE.test(pid)) {
        errors.push(`${loc}.id: must be a pin id matching ` +
          `${AFTER_ID_RE.source} (got ${JSON.stringify(pid)})`);
        return;
      }
      const allowed = o === "add" ? ["op", "id", "text"] : ["op", "id"];
      const extra = Object.keys(op).filter((kk) => !allowed.includes(kk));
      if (extra.length) {
        errors.push(`${loc}: ${o} allows only ${allowed.join("/")} keys (got ` +
          `extra ${extra.sort().join(",")}) — the pin grammar is closed`);
      }
      if (o === "add") {
        const text = op.text;
        if (typeof text !== "string" || !text.trim()) {
          errors.push(`${loc}.text: add requires non-empty text — the rail ` +
            "holds text-only facts");
        } else if (text.length > PIN_TEXT_MAX_LEN) {
          errors.push(`${loc}.text: ${text.length} chars exceeds the ` +
            `${PIN_TEXT_MAX_LEN}-char pin budget — a pinned fact is one line ` +
            "the summary can reuse");
        }
        if (live.has(pid)) {
          errors.push(`${loc}: pin "${pid}" is already on the rail — erase ` +
            "it before re-adding");
        } else if (live.size >= PIN_MAX) {
          errors.push(`${loc}: the rail holds at most ${PIN_MAX} facts — ` +
            `adding "${pid}" would make ${live.size + 1}; erase something first`);
        } else {
          live.set(pid, "live");
        }
      } else if (o === "erase") {
        if (!live.has(pid)) {
          errors.push(`${loc}: erase targets "${pid}" which is not on the ` +
            "rail (never added or already erased)");
        } else {
          live.delete(pid);
        }
      } else { // dim
        if (!live.has(pid)) {
          errors.push(`${loc}: dim targets "${pid}" which is not on the ` +
            "rail (never added or already erased)");
        } else if (live.get(pid) === "dim") {
          warnings.push(`${loc}: pin "${pid}" is already dimmed`);
        } else {
          live.set(pid, "dim");
        }
      }
    });
  });
  return count;
}

/** Validate student tokens in an ALLOWED board-text string → count of
 * well-formed tokens. Any '{{student.' run not covered by a full
 * {{student.<name>|<fallback>}} match is malformed (fallback mandatory). */
function lintTokenText(s, loc, errors) {
  const spans = [];
  let n = 0;
  const re = new RegExp(STUDENT_TOKEN_RE.source, "g");
  let m;
  while ((m = re.exec(s)) !== null) {
    spans.push([m.index, m.index + m[0].length]);
    const name = m[1];
    const fb = m[2];
    if (!STUDENT_TOKEN_NAMES.includes(name)) {
      errors.push(`${loc}: unknown student token "student.${name}" — the ` +
        `namespace is closed (${STUDENT_TOKEN_NAMES.join("|")})`);
    } else if (!fb.trim()) {
      errors.push(`${loc}: student token "student.${name}" has a blank ` +
        "fallback — the fallback is mandatory (a fresh session has no value)");
    } else {
      n += 1;
    }
  }
  let idx = s.indexOf(STUDENT_TOKEN_ATTEMPT);
  while (idx !== -1) {
    if (!spans.some(([a, b]) => a <= idx && idx < b)) {
      errors.push(`${loc}: malformed student token near "{{student." — the ` +
        "only form is {{student.<name>|<fallback>}} (fallback mandatory)");
      break;
    }
    idx = s.indexOf(STUDENT_TOKEN_ATTEMPT, idx + 1);
  }
  return n;
}

/** Walk EVERY string in the lesson and police student tokens (Task #354).
 * Board-text ONLY (board text slots, pin text, echo text) with a closed
 * namespace + mandatory fallback; any key named `say` is narration and
 * token-free (pre-rendered TTS cannot interpolate); mark targets reject
 * tokens (session-dependent text can never match). Python mirror:
 * lesson_anchors._lint_student_tokens. */
export function lintStudentTokens(lesson, where, errors) {
  if (!isObj(lesson)) return 0;
  let count = 0;
  const boardTextKeys = ["kicker", "title", "caption", "term", "meaning", "text"];
  const boardListKeys = ["lines", "steps"];

  const walk = (node, loc, key, depth, inBoard, inMarks, inEcho, inPin) => {
    if (typeof node === "string") {
      if (node.indexOf(STUDENT_TOKEN_ATTEMPT) === -1) return;
      if (key === "say") {
        errors.push(`${loc}: student tokens NEVER appear in narration — TTS ` +
          "is pre-rendered at publish and cannot interpolate (say fields are token-free)");
      } else if (inMarks) {
        errors.push(`${loc}: mark targets cannot carry student tokens — the ` +
          "rendered text is session-dependent and the mark would never find it");
      } else if (inBoard && key === "latex") {
        errors.push(`${loc}: student tokens cannot appear in \`latex\` — the ` +
          "equation source is typeset, not substituted board text");
      } else if ((inBoard && (boardTextKeys.includes(key) || boardListKeys.includes(key))) ||
                 (inEcho && key === "text") || (inPin && key === "text")) {
        count += lintTokenText(node, loc, errors);
      } else {
        errors.push(`${loc}: student tokens are board-text only (board text ` +
          "slots, pin text, echo text) — this field is never substituted at render");
      }
      return;
    }
    if (isObj(node)) {
      for (const [k, v] of Object.entries(node)) {
        const ks = String(k);
        walk(v, `${loc}.${ks}`, ks, depth + 1,
          inBoard || (depth === 0 && ks === "board"),
          inMarks || (inBoard && ks === "marks"),
          inEcho || (depth === 0 && ks === "echo"),
          inPin || (depth === 0 && ks === "pin"));
      }
    } else if (Array.isArray(node)) {
      node.forEach((v, i) => {
        walk(v, `${loc}[${i}]`, key, depth + 1, inBoard, inMarks, inEcho, inPin);
      });
    }
  };

  const beats = Array.isArray(lesson.beats) ? lesson.beats : [];
  beats.forEach((beat, bi) => {
    walk(beat, `${where}.beats[${bi}]`, "", 0, false, false, false, false);
  });
  if (isObj(lesson.remedies)) {
    for (const [rid, rbeat] of Object.entries(lesson.remedies)) {
      walk(rbeat, `${where}.remedies.${rid}`, "", 0, false, false, false, false);
    }
  }
  return count;
}

/** Lesson-level cue lint — JS mirror of lesson_anchors.lint_lesson_cues.
 * published=true is the ship gate: sugar / unpinned word anchors are errors. */
export function lintLessonCues(lesson, { where = "lesson", published = false } = {}) {
  const errors = [];
  const warnings = [];
  let count = 0;
  if (!lesson || typeof lesson !== "object" || Array.isArray(lesson)) {
    return { errors: [`${where}: lesson must be an object`], warnings, count: 0 };
  }
  const beats = Array.isArray(lesson.beats) ? lesson.beats : [];
  const remedies = lesson.remedies;
  let fxCount = 0;
  let idCount = 0;
  let markCount = 0;
  let dirCount = 0;
  let plateCount = 0;
  let holdCount = 0;
  let customCount = 0;
  let echoCount = 0;
  // The per-beat cue lint — shared verbatim by main-line beats and the
  // depth-1 remedy beats in `lesson.remedies` (a remedy performs like any
  // beat, so its board/direction/marks obey the same grammar).
  const lintBeatCues = (beat, locBeat) => {
    for (const { path, container } of collectAnchors(beat)) {
      count += 1;
      const at = container.at;
      const loc = `${locBeat}${path ? "." + path : ""}.at`;
      const { kind, errors: errs } = classifyAnchor(at);
      errs.forEach((e) => errors.push(`${loc}: ${e}`));
      if (errs.length) continue;
      if (published) {
        if (kind === "sugar") {
          errors.push(`${loc}: word-string sugar ${JSON.stringify(at.word)} in published content — ` +
            "resolve to a token index at publish (scripts/resolve_learn_anchors.py)");
        } else if (kind === "word-index" && !isResolvedAnchor(at)) {
          errors.push(`${loc}: unresolved word anchor (no tMs/align pin) in published content — ` +
            "run scripts/resolve_learn_anchors.py");
        }
      }
    }
    // Reveal grammar (Task #348): `fx` is a CLOSED enum; a reveal `id` (the
    // `after` event target) must be a legal board-event id.
    for (const { path, container } of collectFxContainers(beat)) {
      const locBase = `${locBeat}${path ? "." + path : ""}`;
      if ("fx" in container) {
        fxCount += 1;
        const fx = container.fx;
        if (typeof fx !== "string" || !REVEAL_FX.has(fx)) {
          errors.push(`${locBase}.fx: must be one of ${[...REVEAL_FX].map((s) => JSON.stringify(s)).join("|")} ` +
            `(got ${JSON.stringify(fx)}) — the effect enum is closed; runtime owns styling and foley`);
        }
      }
      if ("id" in container) {
        idCount += 1;
        const id = container.id;
        if (typeof id !== "string" || !AFTER_ID_RE.test(id)) {
          errors.push(`${locBase}.id: reveal fragment id must match ${AFTER_ID_RE.source} ` +
            `(got ${JSON.stringify(id)}) — it is the {"after": id} event target`);
        }
      }
    }
    // Mark grammar (Task #350): board.marks is a closed {mark, target, at}
    // array; targets must address text the block declares.
    if (beat && typeof beat === "object" && !Array.isArray(beat)) {
      markCount += lintBoardMarks(beat.board, locBeat, errors, warnings);
      // Direction grammar (Task #351): beat.direction is a closed {verb, at}
      // array — the authored override slot over the derived performance.
      dirCount += lintBeatDirection(beat, locBeat, errors);
      // Plate grammar (Task #352): primary sources on the board — a closed
      // board kind whose asset/regions resolve only against the legend's
      // signed manifest.
      plateCount += lintPlateBoard(beat.board, locBeat, errors);
    }
  };
  beats.forEach((beat, bi) => {
    const locBeat = `${where}.beats[${bi}]`;
    lintBeatCues(beat, locBeat);
    if (beat && typeof beat === "object" && !Array.isArray(beat)) {
      // Hold grammar (Task #353): the ungraded predict-pause — closed
      // shape, server-held reveal, holds cannot nest.
      holdCount += lintHoldBeat(beat, locBeat, errors);
      // Custom-widget grammar (v9): the authored sandboxed widget — closed
      // shape; the html payload is performed ONLY inside the null-origin
      // sandbox (the one lint-exempt string).
      customCount += lintCustomBeat(beat, locBeat, errors);
      // Memory grammar (Task #354): per-distractor echo feedback —
      // server-held (Trap 5), keys on the graded option index.
      echoCount += lintEchoBeat(beat, remedies, locBeat, errors);
    }
  });
  // Memory grammar (Task #354): the depth-1 remedy pool, the pin rail's
  // sequential projection, and the student-token walk.
  const remedyCount = lintLessonRemedies(lesson, where, errors, warnings);
  if (isObj(remedies)) {
    for (const [rid, rbeat] of Object.entries(remedies)) {
      if (isObj(rbeat)) lintBeatCues(rbeat, `${where}.remedies.${rid}`);
    }
  }
  const pinCount = lintLessonPins(lesson, where, errors, warnings);
  const tokenCount = lintStudentTokens(lesson, where, errors);
  const v = lesson.schemaVersion;
  if (fxCount > 0 && (v === undefined || v === null ||
      (typeof v === "number" && Number.isInteger(v) && v < REVEAL_MIN_SCHEMA_VERSION))) {
    errors.push(`${where}: uses reveal \`fx\` but schemaVersion is ${v == null ? "missing" : v} — ` +
      `the reveal grammar requires schemaVersion ${REVEAL_MIN_SCHEMA_VERSION}`);
  }
  if (idCount > 0 && (v === undefined || v === null ||
      (typeof v === "number" && Number.isInteger(v) && v < REVEAL_MIN_SCHEMA_VERSION))) {
    errors.push(`${where}: uses reveal fragment \`id\`s but schemaVersion is ${v == null ? "missing" : v} — ` +
      `board-event sources are reveal grammar and require schemaVersion ${REVEAL_MIN_SCHEMA_VERSION}`);
  }
  if (markCount > 0 && (v === undefined || v === null ||
      (typeof v === "number" && Number.isInteger(v) && v < MARK_MIN_SCHEMA_VERSION))) {
    errors.push(`${where}: uses board \`marks\` but schemaVersion is ${v == null ? "missing" : v} — ` +
      `the mark grammar requires schemaVersion ${MARK_MIN_SCHEMA_VERSION}`);
  }
  if (dirCount > 0 && (v === undefined || v === null ||
      (typeof v === "number" && Number.isInteger(v) && v < DIRECTION_MIN_SCHEMA_VERSION))) {
    errors.push(`${where}: uses beat \`direction\` but schemaVersion is ${v == null ? "missing" : v} — ` +
      `the direction grammar requires schemaVersion ${DIRECTION_MIN_SCHEMA_VERSION}`);
  }
  if (plateCount > 0 && (v === undefined || v === null ||
      (typeof v === "number" && Number.isInteger(v) && v < PLATE_MIN_SCHEMA_VERSION))) {
    errors.push(`${where}: uses \`plate\` boards but schemaVersion is ${v == null ? "missing" : v} — ` +
      `the plate grammar requires schemaVersion ${PLATE_MIN_SCHEMA_VERSION}`);
  }
  if (holdCount > 0 && (v === undefined || v === null ||
      (typeof v === "number" && Number.isInteger(v) && v < HOLD_MIN_SCHEMA_VERSION))) {
    errors.push(`${where}: uses \`hold\` beats but schemaVersion is ${v == null ? "missing" : v} — ` +
      `the hold grammar requires schemaVersion ${HOLD_MIN_SCHEMA_VERSION}`);
  }
  if (customCount > 0 && (v === undefined || v === null ||
      (typeof v === "number" && Number.isInteger(v) && v < CUSTOM_MIN_SCHEMA_VERSION))) {
    errors.push(`${where}: uses \`custom\` widget beats but schemaVersion is ${v == null ? "missing" : v} — ` +
      `the custom-widget grammar requires schemaVersion ${CUSTOM_MIN_SCHEMA_VERSION}`);
  }
  if ((echoCount > 0 || remedyCount > 0) && (v === undefined || v === null ||
      (typeof v === "number" && Number.isInteger(v) && v < MEMORY_MIN_SCHEMA_VERSION))) {
    errors.push(`${where}: uses \`echo\`/\`remedies\` distractor feedback but ` +
      `schemaVersion is ${v == null ? "missing" : v} — the memory grammar requires ` +
      `schemaVersion ${MEMORY_MIN_SCHEMA_VERSION}`);
  }
  if (pinCount > 0 && (v === undefined || v === null ||
      (typeof v === "number" && Number.isInteger(v) && v < MEMORY_MIN_SCHEMA_VERSION))) {
    errors.push(`${where}: uses \`pin\` rail ops but schemaVersion is ${v == null ? "missing" : v} — ` +
      `the memory grammar requires schemaVersion ${MEMORY_MIN_SCHEMA_VERSION}`);
  }
  if (tokenCount > 0 && (v === undefined || v === null ||
      (typeof v === "number" && Number.isInteger(v) && v < MEMORY_MIN_SCHEMA_VERSION))) {
    errors.push(`${where}: uses student tokens but schemaVersion is ${v == null ? "missing" : v} — ` +
      `the memory grammar requires schemaVersion ${MEMORY_MIN_SCHEMA_VERSION}`);
  }
  if (v === undefined || v === null || v === 1) {
    if (count > 0) {
      errors.push(`${where}: uses \`at\` anchors but schemaVersion is ${v == null ? "missing" : v} — ` +
        `anchors require schemaVersion ${ANCHOR_MIN_SCHEMA_VERSION}`);
    }
  } else if (typeof v !== "number" || !Number.isInteger(v) || typeof v === "boolean") {
    errors.push(`${where}: schemaVersion must be an integer (got ${JSON.stringify(v)})`);
  } else if (v < 1) {
    errors.push(`${where}: schemaVersion must be >= 1 (got ${v})`);
  } else if (v > CUE_SCHEMA_VERSION) {
    errors.push(`${where}: schemaVersion ${v} is newer than this validator understands ` +
      `(max ${CUE_SCHEMA_VERSION}) — update the validators/migration lints before shipping a newer grammar`);
  }
  return { errors, warnings, count };
}

// ─────────────────────────────────────────────────────────────────────────────
// WS alignment aggregation (realtime path — chars→words, chunk rebasing)
// ─────────────────────────────────────────────────────────────────────────────

function isCjk(ch) {
  const o = ch.codePointAt(0);
  return (o >= 0x4e00 && o <= 0x9fff) || (o >= 0x3400 && o <= 0x4dbf) ||
         (o >= 0xf900 && o <= 0xfaff) || (o >= 0x3040 && o <= 0x309f) ||
         (o >= 0x30a0 && o <= 0x30ff) || (o >= 0xac00 && o <= 0xd7af);
}

/** Fold a rebased (char,startMs,endMs) stream into word tokens — the same
 * grammar as narration_alignment.chars_to_words (whitespace splits, CJK
 * ideographs/kana/hangul are single-char tokens). */
export function charsToWords(entries) {
  const words = [];
  let cur = "", curStart = 0, curEnd = 0;
  const flush = () => {
    if (cur) words.push({ w: cur, startMs: Math.round(curStart), endMs: Math.round(curEnd) });
    cur = "";
  };
  for (const { ch, startMs, endMs } of entries || []) {
    if (typeof ch !== "string" || !ch.length) continue;
    if (/\s/.test(ch)) { flush(); continue; }
    if (ch.length === 1 && isCjk(ch)) {
      flush();
      words.push({ w: ch, startMs: Math.round(startMs), endMs: Math.round(endMs) });
      continue;
    }
    if (!cur) curStart = startMs;
    cur += ch;
    curEnd = endMs;
  }
  flush();
  return words;
}

/**
 * Aggregates the per-chunk `alignment` messages the ElevenLabs realtime WS
 * sends alongside audio (the payloads RealtimeLLMToElevenLabs used to
 * discard). Vendors have shipped BOTH chunk-relative and utterance-absolute
 * char times, so rebasing is tolerant: if a chunk's first char start would
 * move the clock BACKWARDS past what we've already seen, the chunk is treated
 * as chunk-relative and rebased onto the caller-supplied audio offset
 * (cumulative decoded-audio ms before the chunk); otherwise times are taken
 * as absolute. Word timings from this path feed live/debug consumers only —
 * stored lesson anchors resolve against the HTTP path's hash-pinned
 * alignment, never against a live stream.
 */
export function createWsAlignmentAggregator() {
  const entries = [];
  let lastEndMs = 0;
  let msgs = 0;
  let chars = 0;
  return {
    /** @param al  alignment msg: {chars|characters, charStartTimesMs|char_start_times_ms,
     *             charDurationsMs|char_durations_ms} (all vendor spellings tolerated)
     *  @param audioMsBefore  cumulative audio ms received BEFORE this chunk */
    push(al, audioMsBefore = 0) {
      if (!al || typeof al !== "object") return 0;
      const cs = al.chars || al.characters || [];
      const starts = al.charStartTimesMs || al.char_start_times_ms || [];
      const durs = al.charDurationsMs || al.char_durations_ms || [];
      const n = Math.min(cs.length, starts.length);
      if (!n) return 0;
      const relative = (Number(starts[0]) || 0) + 60 < lastEndMs;
      const offset = relative ? Math.max(audioMsBefore, lastEndMs) : 0;
      for (let i = 0; i < n; i++) {
        const s = offset + (Number(starts[i]) || 0);
        const d = Number(durs[i]) || 0;
        entries.push({ ch: String(cs[i]), startMs: s, endMs: s + d });
        if (s + d > lastEndMs) lastEndMs = s + d;
      }
      msgs += 1;
      chars += n;
      return n;
    },
    words() { return charsToWords(entries); },
    stats() { return { msgs, chars, lastEndMs }; },
    reset() { entries.length = 0; lastEndMs = 0; msgs = 0; chars = 0; },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// The scheduler
// ─────────────────────────────────────────────────────────────────────────────

const now = () => (typeof performance !== "undefined" ? performance.now() : Date.now());

export class CueScheduler {
  constructor({ debug = false, raf = null } = {}) {
    this._debug = !!debug;
    this._raf = raf || ((cb) => (typeof requestAnimationFrame !== "undefined"
      ? requestAnimationFrame(cb) : setTimeout(() => cb(now()), 33)));
    this._performers = new Map();     // kind ("word-index"|"after"|"*") → [fn]
    this._beatIndex = -1;
    this._cues = [];                  // [{id, path, at, kind, resolved, degraded, container}]
    this._timing = null;              // {kind:"words", hash, words} | null
    this._fired = new Set();          // cue ids fired for the CURRENT line
    this._clock = null;               // () => ms | null
    this._lineActive = false;
    this._lineGen = 0;
    this._holdCount = 0; // counted holds (Task #353) — see clock-hold contract
    this._log = [];
    this._wordListeners = [];         // word-boundary subscribers (captions)
    this._lineEndListeners = [];
    this._wordCursor = 0;             // next timing word index to announce
    this._rtSeq = 0;                  // runtime (auto-staged) cue id sequence
  }

  // ── performers ────────────────────────────────────────────────────────────
  /** Performers must be idempotent projections (Trap 3). fn({cue, tMs, mode,
   * beatIndex}) with mode ∈ live|start|flush|project|event. */
  registerPerformer(kind, fn) {
    if (typeof fn !== "function") return () => {};
    const k = kind || "*";
    if (!this._performers.has(k)) this._performers.set(k, []);
    this._performers.get(k).push(fn);
    return () => {
      const arr = this._performers.get(k) || [];
      const i = arr.indexOf(fn);
      if (i >= 0) arr.splice(i, 1);
    };
  }

  get performerCount() {
    let n = 0;
    for (const arr of this._performers.values()) n += arr.length;
    return n;
  }

  /** Word-boundary subscribers (the captions primitive, Task #348). The
   * scheduler is the ONE clock owner, so word-paced captions subscribe here
   * instead of watching the playhead themselves. fn({index, mode}) where
   * `index` is the highest timing-word index reached; mode "live" fires as
   * the playhead crosses each word start, "flush" once when the line ends
   * naturally. No timing / no clock → no word events at all (degradation:
   * whole-line captions). */
  registerWordListener(fn) {
    if (typeof fn !== "function") return () => {};
    this._wordListeners.push(fn);
    return () => {
      const i = this._wordListeners.indexOf(fn);
      if (i >= 0) this._wordListeners.splice(i, 1);
    };
  }

  registerLineEndListener(fn) {
    if (typeof fn !== "function") return () => {};
    this._lineEndListeners.push(fn);
    return () => {
      const i = this._lineEndListeners.indexOf(fn);
      if (i >= 0) this._lineEndListeners.splice(i, 1);
    };
  }

  _fireWord(index, mode) {
    const payload = { index, mode, beatIndex: this._beatIndex };
    for (const fn of this._wordListeners) {
      try { fn(payload); } catch (e) { this._dbg("word listener threw", { e: String(e) }); }
    }
  }

  // ── beat lifecycle ────────────────────────────────────────────────────────
  /** Collect this beat's anchors. Called by the controller on every _goTo —
   * a full reset, which is what makes seek deterministic (state is rebuilt,
   * never resumed). */
  armBeat(beatIndex, beat) {
    this.endLine("re-arm");
    this._beatIndex = beatIndex;
    this._timing = null;
    this._fired = new Set();
    this._cues = collectAnchors(beat).map(({ path, container }, i) => {
      const at = container.at;
      const { kind, errors } = classifyAnchor(at);
      return {
        id: `b${beatIndex}:${path || "beat"}#${i}`,
        path, container, at,
        kind: errors.length ? "invalid" : kind,
        resolved: !errors.length && isResolvedAnchor(at) && kind === "word-index",
        degraded: false,
      };
    });
    this._dbg("armBeat", { beatIndex, cues: this._cues.length });
    return this._cues.length;
  }

  /** Runtime-derived cues (Task #348 default staging): the controller derives
   * tasteful reveal moments for fragments that carry NO authored anchor, pinned
   * against the CURRENT line's live alignment (same-source, so they can never
   * rot). Call AFTER setLineTiming and BEFORE beginLine. Cleared by the next
   * armBeat like every other cue. Authored anchors always win — the controller
   * only derives cues for anchor-less fragments. */
  armRuntimeCues(list) {
    if (!Array.isArray(list) || !list.length) return 0;
    let n = 0;
    for (const item of list) {
      if (!item || typeof item !== "object" || !item.container) continue;
      const at = item.at;
      if (!at || typeof at.word !== "number" || typeof at.tMs !== "number" ||
          typeof at.align !== "string") continue;
      this._cues.push({
        id: `b${this._beatIndex}:auto#${this._rtSeq++}`,
        path: item.path || `auto#${n}`,
        container: item.container,
        at,
        kind: "word-index",
        resolved: true,
        degraded: false,
        runtime: true,
      });
      n++;
    }
    if (n) this._dbg("armRuntimeCues", { beatIndex: this._beatIndex, added: n });
    return n;
  }

  /** Attach the narration line's timing_data ({kind:"words", hash, words} or
   * null). Verifies every pinned cue against the hash — a mismatch is anchor
   * rot at play (audio regenerated after publish, or per-deploy narration
   * cache wiped): LOUD in the log, graceful on the board (cue → line start). */
  setLineTiming(timing) {
    const ok = timing && timing.kind === "words" &&
      typeof timing.hash === "string" && Array.isArray(timing.words);
    this._timing = ok ? timing : null;
    for (const cue of this._cues) {
      if (cue.kind !== "word-index") continue;
      if (!cue.resolved) { cue.degraded = true; continue; }
      cue.degraded = !ok || cue.at.align !== timing.hash;
      if (ok && cue.at.align !== timing.hash) {
        this._dbg("ANCHOR ROT — align pin does not match live narration; cue fires at line start", {
          cue: cue.id, pinned: cue.at.align, live: timing.hash,
        });
      }
    }
    return this._timing != null;
  }

  /** Begin the narration line. clockFn returns the audio playhead in ms (the
   * SAME time base the lip-sync path anchors to — audio element currentTime /
   * AudioContext playhead), or null while not yet determinable. clockFn=null
   * means "no clock" (no-audio/preview/estimate path): every word cue fires
   * immediately at line start — the degradation law's behave-as-today. */
  beginLine(clockFn) {
    this.endLine("restart");
    const gen = ++this._lineGen;
    this._lineActive = true;
    this._clock = typeof clockFn === "function" ? clockFn : null;
    this._wordCursor = 0;

    // Degraded + unresolved word cues fire at line start regardless of clock.
    for (const cue of this._cues) {
      if (cue.kind === "invalid" || cue.kind === "after" || cue.kind === "widget-event") continue;
      const startFire = !this._clock || !this._timing || cue.kind === "sugar" ||
        !cue.resolved || cue.degraded;
      if (startFire && !this._fired.has(cue.id)) {
        this._fire(cue, 0, "start");
      }
    }
    if (!this._clock) { this._lineActive = false; return; }

    const step = () => {
      if (!this._lineActive || gen !== this._lineGen) return;
      const t = this._clock ? this._clock() : null;
      if (t != null && isFinite(t)) {
        for (const cue of this._cues) {
          if (!cue.resolved || cue.degraded || this._fired.has(cue.id)) continue;
          if (cue.at.tMs <= t) this._fire(cue, t, "live");
        }
        // Word boundaries for the captions primitive (Task #348) — same clock,
        // same tick, so captions can never drift from the cues.
        const words = this._timing && this._timing.words;
        if (words && this._wordListeners.length) {
          while (this._wordCursor < words.length &&
                 (words[this._wordCursor].startMs || 0) <= t) {
            this._fireWord(this._wordCursor, "live");
            this._wordCursor++;
          }
        }
      }
      this._raf(step);
    };
    this._raf(step);
    this._dbg("beginLine", { beatIndex: this._beatIndex, gen, timed: this._timedCueCount() });
  }

  /** End the line. reason "ended" (audio finished naturally) FLUSHES the
   * still-pending timed cues so the board always reaches its final state —
   * the flush is part of the projection (projectAt(∞) fires the same set).
   * Any other reason (interrupt/seek/re-arm) just stops: the controller is
   * about to rebuild state via _revealUpTo + projectAt. */
  endLine(reason = "ended") {
    if (!this._lineActive && reason !== "ended") { this._clock = null; return; }
    const wasActive = this._lineActive;
    this._lineActive = false;
    this._lineGen++;
    this._clock = null;
    if (reason === "ended" && wasActive) {
      for (const cue of this._cues) {
        if (cue.resolved && !cue.degraded && !this._fired.has(cue.id)) {
          this._fire(cue, Infinity, "flush");
        }
      }
      // Captions reach end-of-line with the board (word cursor → final word).
      const words = this._timing && this._timing.words;
      if (words && words.length && this._wordListeners.length &&
          this._wordCursor < words.length) {
        this._fireWord(words.length - 1, "flush");
        this._wordCursor = words.length;
      }
    }
    if (wasActive) this._dbg("endLine", { reason });
    for (const fn of this._lineEndListeners) {
      try { fn({ reason, beatIndex: this._beatIndex, wasActive }); }
      catch (e) { this._dbg("line-end listener threw", { e: String(e) }); }
    }
  }

  /** Pure projection (Trap 3): the set of word cues due at playhead t for the
   * current beat, re-fired in "project" mode. Deterministic and idempotent —
   * replay/prev/progress-jump rebuild exact board state from
   * (playhead, graded answers) with no dependence on what fired live.
   * `after` cues are runtime events and project only once their event has
   * been recorded via onBoardEvent (the reveal primitive owns that log). */
  projectAt(tMs) {
    const due = [];
    for (const cue of this._cues) {
      if (cue.kind === "invalid" || cue.kind === "after" || cue.kind === "widget-event") continue;
      const atStart = !cue.resolved || cue.degraded || !this._timing;
      if (atStart || cue.at.tMs <= tMs) due.push(cue);
    }
    for (const cue of due) this._fire(cue, tMs, "project", /*force*/ true);
    return due.map((c) => c.id);
  }

  /** Board-event anchors ({"after": id}) — fired by the runtime when the named
   * board event lands (reveal primitive plumbs this later). */
  onBoardEvent(eventId) {
    let n = 0;
    for (const cue of this._cues) {
      if (cue.kind !== "after" || this._fired.has(cue.id)) continue;
      if (cue.at.after === eventId) { this._fire(cue, null, "event"); n++; }
    }
    return n;
  }

  onWidgetEvent(eventName) {
    if (typeof eventName !== "string" || !WIDGET_EVENT_RE.test(eventName)) return 0;
    let n = 0;
    for (const cue of this._cues) {
      if (cue.kind !== "widget-event" || this._fired.has(cue.id)) continue;
      if (cue.at.event === eventName) { this._fire(cue, null, "event"); n++; }
    }
    return n;
  }

  isLineActive() { return this._lineActive; }

  // ── clock-hold contract (see header) ──────────────────────────────────────
  // Counted (Task #353): each holder pairs its own hold()/release(); the clock
  // is held while ANY holder is outstanding. release() floors at zero so a
  // stray double-release can never push the count negative.
  hold() {
    this._holdCount += 1;
    if (this._holdCount === 1) this._dbg("hold", {});
  }
  release() {
    if (this._holdCount > 0) {
      this._holdCount -= 1;
      if (this._holdCount === 0) this._dbg("release", {});
    }
  }
  isHeld() { return this._holdCount > 0; }

  reset(reason = "reset") {
    this.endLine(reason);
    this._cues = [];
    this._timing = null;
    this._fired = new Set();
    this._beatIndex = -1;
  }

  // ── internals ─────────────────────────────────────────────────────────────
  _timedCueCount() {
    return this._cues.filter((c) => c.resolved && !c.degraded).length;
  }

  _fire(cue, tMs, mode, force = false) {
    if (!force) {
      if (this._fired.has(cue.id)) return;
      this._fired.add(cue.id);
    }
    const payload = { cue, tMs, mode, beatIndex: this._beatIndex };
    const run = (fn) => { try { fn(payload); } catch (e) { this._dbg("performer threw", { cue: cue.id, e: String(e) }); } };
    (this._performers.get(cue.kind) || []).forEach(run);
    (this._performers.get("*") || []).forEach(run);
    this._dbg("fire", { cue: cue.id, kind: cue.kind, tMs, mode });
  }

  _dbg(msg, data) {
    if (!this._debug) return;
    const entry = { t: Math.round(now()), msg, ...data };
    this._log.push(entry);
    if (this._log.length > 400) this._log.splice(0, this._log.length - 400);
    // The esbuild bundle strips console.*, so the log array is the real
    // debug surface (window.__cueLog when the controller enables debug).
    try { console.info("[CUE]", msg, data); } catch (_) { /* stripped in prod */ }
  }

  get log() { return this._log; }
  stats() {
    return {
      beatIndex: this._beatIndex,
      cues: this._cues.length,
      timed: this._timedCueCount(),
      fired: this._fired.size,
      held: this._holdCount > 0,
      holdCount: this._holdCount,
      lineActive: this._lineActive,
      hasTiming: !!this._timing,
      performers: this.performerCount,
    };
  }
}

export default CueScheduler;
